import { createContext, useContext, useId, useState, type ReactNode } from 'react'
import { Button } from '../../ui/Button'
import styles from './Settings.module.css'

/**
 * A titled card that reads until you press Edit, then writes.
 *
 * The old Settings gave every group its own Edit / Save / Cancel and disabled
 * inputs in between, which is worth keeping: these are rarely-changed details,
 * and a screen of live inputs invites an accidental edit with no way back. Each
 * card owns its own draft, so saving Company cannot carry a half-typed Personal
 * field with it.
 */
/**
 * Which cards are open, held by the page rather than by each card.
 *
 * It cannot live inside the card: saving refetches the profile, the page re-keys
 * the card on the new `dataUpdatedAt` so its draft rebuilds from the saved
 * values, and a re-keyed component is a NEW component with fresh state. Held
 * here, the card the user opened stays open when they save it — held inside, it
 * shut itself the moment the save came back, which read as the save having
 * thrown the screen away.
 */
const OpenSections = createContext<{ isOpen: (title: string) => boolean; toggle: (title: string) => void } | null>(null)

export function OpenSectionsProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set())
  return (
    <OpenSections.Provider
      value={{
        isOpen: (title) => open.has(title),
        toggle: (title) =>
          setOpen((current) => {
            const next = new Set(current)
            if (!next.delete(title)) next.add(title)
            return next
          }),
      }}
    >
      {children}
    </OpenSections.Provider>
  )
}

export function SettingsCard({
  title,
  description,
  summary = [],
  editable = true,
  editing,
  onEdit,
  onCancel,
  onSave,
  saving = false,
  error,
  danger = false,
  children,
}: {
  title: string
  description: string
  /**
   * What this card currently holds, as chips, shown while it is closed.
   *
   * The point of the grid is that the account is readable without opening
   * anything: eight cards each saying what they contain beats eight cards each
   * saying only what they are for. Empty chips are dropped by the caller, not
   * rendered as "not set" — a card is allowed to say nothing yet.
   */
  summary?: readonly string[]
  editable?: boolean
  editing?: boolean
  onEdit?: () => void
  onCancel?: () => void
  onSave?: () => void
  saving?: boolean
  error?: string | null
  /** Delete account. Red title, and it opens like the rest. */
  danger?: boolean
  children: ReactNode
}) {
  // A generated id, not the title: aria-labelledby takes a space-separated LIST
  // of ids, so "Export data-heading" was read as two ids that do not exist and
  // the card had no accessible name at all. Single-word cards happened to work,
  // which is how it survived a visual review.
  const headingId = useId()
  const panelId = useId()
  // Closed by default, and an editing card is open by definition: cancelling an
  // edit must not leave the fields on screen with no way to save them.
  const sections = useContext(OpenSections)
  const expanded = (sections?.isOpen(title) ?? false) || Boolean(editing)

  return (
    <section className={`${styles.card} ${expanded ? styles.cardOpen : ''}`} aria-labelledby={headingId}>
      {/* The heading stays a heading and its accessible name stays the title.
          An earlier version put the title in a span inside the button, which
          reads identically on screen and removes the section from every
          heading list a screen reader offers — and from the tests that look
          for one. The disclosure button lives INSIDE the heading, which is the
          standard pattern; a heading is not allowed inside a button. */}
      <h2 className={styles.cardHeading}>
        <button
          type="button"
          id={headingId}
          className={`${styles.cardHead} ${danger ? styles.cardTitleDanger : ''}`}
          aria-expanded={expanded}
          aria-controls={panelId}
          onClick={() => sections?.toggle(title)}
        >
          <span className={styles.cardTitle}>{title}</span>
          <span className={styles.chevron} aria-hidden="true">
            {expanded ? '⌃' : '›'}
          </span>
        </button>
      </h2>
      <p className={styles.cardDescription}>{description}</p>

      {!expanded && summary.length > 0 ? (
        <ul className={styles.summary}>
          {summary.map((chip) => (
            <li key={chip} className={styles.summaryChip}>
              {chip}
            </li>
          ))}
        </ul>
      ) : null}

      <div id={panelId} className={styles.fields} hidden={!expanded}>
        {children}
      </div>

      {error && expanded ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}

      {editable && expanded ? (
        <div className={styles.actions}>
          {editing ? (
            <>
              <Button onClick={onSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
              <Button onClick={onCancel} disabled={saving}>
                Cancel
              </Button>
            </>
          ) : (
            <Button onClick={onEdit}>Edit</Button>
          )}
        </div>
      ) : null}
    </section>
  )
}

/** A read-only row: a label and a stored value the user cannot type into. */
export function ReadOnlyRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className={styles.readRow}>
      <span className={styles.readLabel}>{label}</span>
      <span className={styles.readValue}>{value}</span>
    </div>
  )
}

/**
 * Draft state for one card: a copy of the saved values, edited freely, and
 * discarded on Cancel. Returns the draft rather than mutating the query cache,
 * so an abandoned edit never reaches anything else on the page.
 */
export function useDraft<T extends object>(initial: T) {
  const [draft, setDraft] = useState<T>(initial)
  const [editing, setEditing] = useState(false)

  return {
    draft,
    editing,
    set: <K extends keyof T>(key: K, value: T[K]) => setDraft((d) => ({ ...d, [key]: value })),
    start: () => {
      setDraft(initial)
      setEditing(true)
    },
    cancel: () => {
      setDraft(initial)
      setEditing(false)
    },
    done: () => setEditing(false),
  }
}
